/**
 * VS-02 / VS-03: what the capture surface CALLS the premises and which privacy floor it takes — read from the server,
 * never from a list kept here. A be with a v2 (pack-driven) taxonomy sends `premisesKindLabel` and
 * `premisesPrivacyClass` on the exchange summary; an older be, or an India case, sends neither and the deck's words for
 * the six original kinds are used. A kind the deck does not know (FARM, SHOPFLOOR, a Gulf kind on an older be) is shown
 * in plain words, never as a raw `sv.door.kind.*` key.
 */
import type { ExchangeSummary } from '@/api/capture';

type Translate = (key: string, vars?: Record<string, string>) => string;

/**
 * The label of record: the server's (VC-12 r5: in the customer's language when the pack serves one —
 * `premisesKindLabelI18n[locale]`), else the deck's, else the kind word made readable.
 */
export function premisesKindText(
  summary: Pick<ExchangeSummary, 'premisesKind' | 'premisesKindLabel' | 'premisesKindLabelI18n'>,
  t: Translate,
  locale?: string,
): string {
  const inLocale = locale ? summary.premisesKindLabelI18n?.[locale]?.trim() : undefined;
  if (inLocale) return inLocale;
  const served = summary.premisesKindLabel?.trim();
  if (served) return served;
  const key = `sv.door.kind.${summary.premisesKind}`;
  const deck = t(key);
  if (deck !== key) return deck;
  const words = String(summary.premisesKind ?? '').toLowerCase().replace(/_/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : t('sv.door.kind.OTHER');
}

/**
 * True when the premises is somewhere people live — the dwelling floor: an India RESIDENCE, or any kind the server
 * classes DWELLING (labour accommodation). Keyed on the class, never on a Gulf kind name.
 */
export function isDwelling(summary: Pick<ExchangeSummary, 'premisesKind' | 'premisesPrivacyClass'>): boolean {
  return summary.premisesPrivacyClass === 'DWELLING' || summary.premisesKind === 'RESIDENCE';
}

/** A dwelling the server named that is not an India home (worker accommodation) — its own guidance card. */
export function isSharedAccommodation(summary: Pick<ExchangeSummary, 'premisesKind' | 'premisesPrivacyClass'>): boolean {
  return summary.premisesPrivacyClass === 'DWELLING' && summary.premisesKind !== 'RESIDENCE';
}
